import { afterNextRender, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';

import { register } from 'swiper/element';

// import { TourStepTemplateComponent } from 'ngx-ui-tour-md-menu';
import { NgxLoadingBar } from '@ngx-loading-bar/core';
import { TranslateService } from '@ngx-translate/core';

import { ApplicationConfiguration } from '@administration/shared/models/application-configuration.interface';
import { ArrayUtilities, DocumentUtilities } from '@tess-core/shared';
import { NavigationComponent } from '@layout/navigation/navigation/navigation.component';
import { OverlayComponent } from '@core/overlay/overlay.component';
import { ResizeObserverService } from '@core/observers/resize-observer/services/resize-observer.service';
import { RouterUtilService } from '@core/services/router-util.service';
import { TessSignalBusService } from '@core/services/tess-signal-bus.service';
import { ThemeService } from '@core/services/theme.service';
import { ToolbarComponent } from '@layout/toolbar/toolbar/toolbar.component';

@Component({
  selector: 'tess-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, NgxLoadingBar, NavigationComponent, ToolbarComponent, OverlayComponent],
  providers: [ResizeObserverService],
  host: {
    class: 'tess-root',
  },
})
export class AppComponent implements OnInit {
  readonly #resizeObserverService = inject(ResizeObserverService);
  readonly #router = inject(Router);
  readonly #routerUtils = inject(RouterUtilService);
  readonly #translate = inject(TranslateService);
  readonly #tessSignalBusService = inject(TessSignalBusService);
  readonly theme = inject(ThemeService);

  // readonly tourStepTemplateComponent = viewChild<TourStepTemplateComponent>(TourStepTemplateComponent);

  protected readonly closedSidebar = signal(false);
  protected readonly activeOutlet = signal<'primary' | 'detail' | 'subdetail' | 'quaternary'>('primary');

  #currentRoute = '';
  #currentRouteOutlets: string[] = [];

  // As the router loads modules asynchronously (via loadChildren), we're going to
  // keep track of how many asynchronous requests are currently active. If there is
  // at least one pending load request, we'll show the indicator.
  #asyncLoadCount = 0;

  #routerEvents = toSignal(this.#router.events);
  #showMenu = false;

  protected readonly routeOptions = computed(() => {
    const event = this.#routerEvents();

    // The Router emits special events for "loadChildren" configuration loading. We
    // just need to listen for the Start and End events in order to determine if we
    // have any pending configuration requests.
    if (event instanceof RouteConfigLoadStart) {
      this.#asyncLoadCount++;
    } else if (event instanceof RouteConfigLoadEnd) {
      this.#asyncLoadCount--;
    } else if (event instanceof NavigationStart) {
      this.#asyncLoadCount++;
    } else if (event instanceof NavigationEnd) {
      this.#asyncLoadCount = 0;

      const currentRouteOutlets = [...this.#routerUtils.getActiveOutlets().keys()];
      // Compare the outlets.
      const equality = ArrayUtilities.compare(currentRouteOutlets, this.#currentRouteOutlets);
      // Trigger a scroll event in the event the outlets match entirely, without taking into account the primary outlet.
      if (currentRouteOutlets.length > 1 && equality) {
        DocumentUtilities.scrollToTop('tess-page-content tess-scrollbar');
      }

      this.#currentRouteOutlets = currentRouteOutlets;
      // Keep track of the current route.
      this.#currentRoute = event.url;
      this.#showMenu = !this.#currentRoute.includes('authentication');
    } else if (event instanceof NavigationCancel) {
      this.#asyncLoadCount = 0;
    } else if (event instanceof NavigationError) {
      // Present error to user
      this.#asyncLoadCount = 0;
    }

    // If there is at least one pending asynchronous config load request,
    // then let's show the loading indicator.
    // --
    // CAUTION: The CSS includes a small 100ms delay such that the loading
    // indicator won't be seen by users with sufficiently fast connections.
    return { asyncLoadCount: this.#asyncLoadCount > 0, showMenu: this.#showMenu };
  });

  constructor() {
    afterNextRender(() => register());

    this.#resizeObserverService.subscribe((entries: ResizeObserverEntry[]): void => {
      // Emit only the last entry.
      this.#tessSignalBusService.set(ApplicationConfiguration.CACHE_KEY_RESIZE_OBSERVER, entries?.pop());
    });

    effect(() => {
      if (this.theme.theme()) {
        document.body.setAttribute('data-theme', this.theme.theme());
      }
    });
  }

  ngOnInit(): void {
    this.#translate.addLangs(['en', 'fr']);
    this.#translate.setDefaultLang('en');
    const browserLang = this.#translate.getBrowserLang();
    this.#translate.use(browserLang.match(/en|fr/) ? browserLang : 'en');

    // Trigger initial navigation since we are bootstrapping the application manually.
    this.#router.initialNavigation();
  }

  // ngAfterViewInit(): void {
  //   this.#communicationService.tourStepTemplateComponent = this.tourStepTemplateComponent();
  // }

  activateRouteOutlet(): void {
    this.#setActiveOutlet();
  }

  deactivateRouteOutlet(): void {
    this.#setActiveOutlet();
  }

  #setActiveOutlet(): void {
    // Retrieve all active outlet.
    const outlets = this.#routerUtils.getActiveOutlets();

    if (outlets.get('quaternary')) {
      this.activeOutlet.set('quaternary');
    } else if (outlets.get('subdetail')) {
      this.activeOutlet.set('subdetail');
    } else if (outlets.get('detail')) {
      this.activeOutlet.set('detail');
    } else {
      this.activeOutlet.set('primary');
    }
  }
}
